<template>
	<div v-loading="loading" class="shopping-cart-page" element-loading-text="正在加载购物车...">
		<header class="cart-page-heading">
			<div>
				<h1>我的购物车</h1>
				<p>管理已加入购物车的商品，选择需要的商品进行结算</p>
			</div>
			<div class="cart-search">
				<el-input
					v-model.trim="searchKeyword"
					clearable
					placeholder="请输入商品名称"
					prefix-icon="el-icon-search"
					@keyup.enter.native="handleSearch"
					@clear="handleSearch"
				></el-input>
				<el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
			</div>
		</header>

		<div class="cart-layout">
			<section class="cart-goods-column">
				<div class="cart-toolbar">
					<div>
						<el-checkbox :value="allSelected" :indeterminate="isIndeterminate" @change="toggleAllSelection">
							全选
						</el-checkbox>
						<span>共 {{ cartItems.length }} 件商品</span>
					</div>
					<button type="button" :disabled="!selectedItems.length" @click="handleBatchRemove">
						<i class="el-icon-delete"></i>
						移出购物车
					</button>
				</div>

				<div v-if="filteredMerchantGroups.length" class="merchant-list">
					<article v-for="merchant in filteredMerchantGroups" :key="merchant.merchantId" class="merchant-card">
						<header class="merchant-heading">
							<el-checkbox
								:value="isMerchantAllSelected(merchant)"
								:indeterminate="isMerchantIndeterminate(merchant)"
								@change="value => toggleMerchantSelection(merchant, value)"
							></el-checkbox>
							<span class="merchant-icon"><i class="el-icon-office-building"></i></span>
							<strong>{{ merchant.merchantName }}</strong>
							<span v-if="merchant.selfOperated" class="self-tag">平台自营</span>
							<span class="merchant-count">{{ merchant.goodsList.length }} 件商品</span>
						</header>

						<div class="goods-column-heading">
							<span>商品信息</span>
							<span>单价</span>
							<span>数量</span>
							<span>小计</span>
							<span>操作</span>
						</div>

						<div
							v-for="goods in merchant.goodsList"
							:key="goods.cartItemId"
							:class="['cart-goods-row', { 'is-invalid': !goods.available }]"
						>
							<div class="goods-information" @click="handleGoodsClick(goods)">
								<el-checkbox
									:value="goods.selected"
									:disabled="!goods.available || isUpdating(goods.cartItemId)"
									@click.native.stop
									@change="value => toggleGoodsSelection(goods, value)"
								></el-checkbox>
								<div class="goods-image">
									<img :src="goods.image" :alt="goods.title" @error="handleImageError" />
								</div>
								<div class="goods-copy">
									<h3>{{ goods.title }}</h3>
									<p>{{ goods.introduction }}</p>
									<span>{{ goods.category }}</span>
									<em v-if="!goods.available" class="invalid-reason">{{ goods.invalidReason || '商品暂不可购买' }}</em>
								</div>
							</div>
							<div class="unit-price">
								<small>¥</small>
								{{ formatAmount(goods.unitPrice) }}
							</div>
							<div class="quantity-control">
								<button
									type="button"
									:disabled="!goods.available || goods.quantity <= 1 || isUpdating(goods.cartItemId)"
									@click="changeQuantity(goods, -1)"
								>
									−
								</button>
								<input
									:value="goods.quantity"
									type="number"
									min="1"
									max="99"
									:disabled="!goods.available || isUpdating(goods.cartItemId)"
									@change="handleQuantityInput(goods, $event)"
								/>
								<button
									type="button"
									:disabled="!goods.available || goods.quantity >= 99 || isUpdating(goods.cartItemId)"
									@click="changeQuantity(goods, 1)"
								>
									+
								</button>
							</div>
							<div class="subtotal">
								<small>¥</small>
								{{ formatAmount(goods.unitPrice * goods.quantity) }}
							</div>
							<button type="button" class="remove-button" :disabled="isUpdating(goods.cartItemId)" @click="handleRemove(goods)">
								<i class="el-icon-delete"></i>
								移出
							</button>
						</div>
					</article>
				</div>

				<div v-else class="cart-empty">
					<span><i class="el-icon-shopping-cart-2"></i></span>
					<strong>{{ appliedKeyword ? '未搜索到相关商品' : '购物车还是空的' }}</strong>
					<p>{{ appliedKeyword ? '请尝试更换商品名称重新搜索' : '去服务商城挑选适合您的商品吧' }}</p>
					<el-button type="primary" plain @click="handleEmptyAction">
						{{ appliedKeyword ? '清除搜索' : '去逛逛' }}
					</el-button>
				</div>
			</section>

			<aside class="cart-settlement-column">
				<section class="settlement-card">
					<header class="settlement-heading">
						<div>
							<h2>结算信息</h2>
							<p>已选择 {{ selectedItems.length }} 件商品</p>
						</div>
						<button v-if="selectedItems.length > 3" type="button" @click="toggleSelectedGoods">
							{{ showAllSelectedGoods ? '收起' : '显示全部商品' }}
							<i :class="showAllSelectedGoods ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
						</button>
					</header>

					<div v-if="selectedItems.length" class="selected-goods-list">
						<div v-for="goods in selectedPreviewItems" :key="goods.cartItemId" class="selected-goods-card">
							<img :src="goods.image" :alt="goods.title" @error="handleImageError" />
							<div>
								<strong :title="goods.title">{{ goods.title }}</strong>
								<span>¥ {{ formatAmount(goods.unitPrice) }} × {{ goods.quantity }}</span>
							</div>
							<button type="button" title="取消选择" @click="toggleGoodsSelection(goods, false)">
								<i class="el-icon-close"></i>
							</button>
						</div>
					</div>
					<div v-else class="selected-empty">
						<i class="el-icon-shopping-bag-1"></i>
						<span>请选择需要结算的商品</span>
					</div>

					<div class="coupon-section">
						<div class="section-title">
							<span>
								<i class="el-icon-tickets"></i>
								优惠券
							</span>
							<em>{{ usableCouponCount }} 张可用</em>
						</div>
						<div v-if="couponEnabled && couponList.length" class="coupon-list">
							<label
								v-for="coupon in couponList"
								:key="coupon.id"
								:class="['coupon-option', { active: selectedCouponId === coupon.id, disabled: !isCouponUsable(coupon) }]"
							>
								<input v-model="selectedCouponId" type="radio" :value="coupon.id" :disabled="!isCouponUsable(coupon)" />
								<span>{{ coupon.name }}</span>
								<strong>{{ coupon.amount ? `-¥${coupon.amount}` : '不使用' }}</strong>
							</label>
						</div>
						<div v-else class="coupon-placeholder">
							<i class="el-icon-tickets"></i>
							<span>暂无可用优惠券，领券功能开放后可在此使用</span>
						</div>
					</div>

					<div class="amount-summary">
						<div>
							<span>商品金额</span>
							<strong>¥ {{ formatAmount(selectedGoodsAmount) }}</strong>
						</div>
						<div class="discount-row">
							<span>优惠减免</span>
							<strong>- ¥ {{ formatAmount(discountAmount) }}</strong>
						</div>
					</div>

					<div class="payable-summary">
						<span>应付金额</span>
						<div>
							<small>¥</small>
							<strong>{{ formatAmount(payableAmount) }}</strong>
						</div>
					</div>

					<el-button
						type="primary"
						class="checkout-button"
						:loading="settling"
						:disabled="!selectedItems.length || settling"
						@click="handleCheckout"
					>
						去结算（{{ selectedItems.length }}）
					</el-button>
					<p class="settlement-tip">
						<i class="el-icon-lock"></i>
						订单信息将在结算页面再次确认
					</p>
				</section>
			</aside>
		</div>
	</div>
</template>

<script>
	import {
		batchRemoveMerchantShoppingCart,
		createMerchantCheckout,
		previewMerchantFile,
		queryMerchantShoppingCart,
		removeMerchantShoppingCart,
		updateMerchantShoppingCartChecked,
		updateMerchantShoppingCartQuantity
	} from '@/api/mall'
	import { createUuid } from '@/utils'

	const DEFAULT_GOODS_IMAGE = require('@/assets/mall/goods/goods-demo.png')

	export default {
		name: 'ShoppingCart',
		data() {
			return {
				loading: false,
				settling: false,
				checkoutRequestId: '',
				checkoutRequestSignature: '',
				updatingIds: [],
				searchKeyword: '',
				appliedKeyword: '',
				showAllSelectedGoods: false,
				selectedCouponId: '',
				couponEnabled: false,
				merchantGroups: [],
				couponList: []
			}
		},
		computed: {
			cartItems() {
				return this.merchantGroups.reduce((list, merchant) => list.concat(merchant.goodsList), [])
			},
			filteredMerchantGroups() {
				if (!this.appliedKeyword) return this.merchantGroups.filter(merchant => merchant.goodsList.length)
				const keyword = this.appliedKeyword.toLowerCase()
				return this.merchantGroups
					.map(merchant => ({
						...merchant,
						goodsList: merchant.goodsList.filter(goods =>
							String(goods.title || '')
								.toLowerCase()
								.includes(keyword)
						)
					}))
					.filter(merchant => merchant.goodsList.length)
			},
			selectedItems() {
				return this.cartItems.filter(goods => goods.selected && goods.available)
			},
			selectedPreviewItems() {
				return this.showAllSelectedGoods ? this.selectedItems : this.selectedItems.slice(0, 3)
			},
			allSelected() {
				const availableItems = this.cartItems.filter(goods => goods.available)
				return availableItems.length > 0 && availableItems.every(goods => goods.selected)
			},
			isIndeterminate() {
				return this.selectedItems.length > 0 && !this.allSelected
			},
			selectedGoodsAmount() {
				return this.selectedItems.reduce((total, goods) => total + Number(goods.unitPrice) * Number(goods.quantity), 0)
			},
			selectedCoupon() {
				return this.couponList.find(coupon => coupon.id === this.selectedCouponId)
			},
			discountAmount() {
				return this.selectedCoupon && this.isCouponUsable(this.selectedCoupon) ? Number(this.selectedCoupon.amount) : 0
			},
			payableAmount() {
				return Math.max(this.selectedGoodsAmount - this.discountAmount, 0)
			},
			usableCouponCount() {
				return this.couponList.filter(coupon => coupon.amount > 0 && this.isCouponUsable(coupon)).length
			}
		},
		watch: {
			selectedItems() {
				if (this.selectedItems.length <= 3) this.showAllSelectedGoods = false
			}
		},
		created() {
			this.loadCart()
		},
		methods: {
			async loadCart() {
				this.loading = true
				try {
					const response = await queryMerchantShoppingCart()
					this.applyCartData(response.data || {})
				} finally {
					this.loading = false
				}
			},
			// 将后端分组结构映射成页面展示字段，商品价格只使用接口实时值。
			applyCartData(data) {
				this.couponEnabled = Boolean(data.couponEnabled)
				this.couponList = Array.isArray(data.couponList) ? data.couponList : []
				this.selectedCouponId = ''
				this.merchantGroups = (data.merchantGroups || []).map(group => ({
					merchantId: group.shopId || 'platform',
					merchantName: group.merchantName || '平台自营',
					selfOperated: Boolean(group.selfOperated),
					goodsList: (group.goodsList || []).map(goods => ({
						cartItemId: goods.cartItemId,
						goodsId: goods.goodsId,
						goodsType: Number(goods.goodsType || 1),
						image: goods.mainImage ? previewMerchantFile() + goods.mainImage : DEFAULT_GOODS_IMAGE,
						title: goods.goodsTitle || '商品已失效',
						introduction: goods.guideTitle || '',
						category: goods.categoryName || '综合服务',
						priceUnit: goods.priceUnit || '元',
						measureUnit: goods.measureUnit || '件',
						unitPrice: Number(goods.unitPrice || 0),
						quantity: Number(goods.quantity || 1),
						selected: Boolean(goods.checked),
						available: Boolean(goods.available),
						invalidReason: goods.invalidReason || ''
					}))
				}))
			},
			handleSearch() {
				this.appliedKeyword = this.searchKeyword
			},
			handleEmptyAction() {
				if (this.appliedKeyword) {
					this.searchKeyword = ''
					this.appliedKeyword = ''
					return
				}
				this.$router.push('/mallStore/index')
			},
			async toggleAllSelection(value) {
				await this.updateChecked(
					this.cartItems.filter(goods => goods.available),
					value
				)
			},
			isMerchantAllSelected(merchant) {
				const availableItems = merchant.goodsList.filter(goods => goods.available)
				return availableItems.length > 0 && availableItems.every(goods => goods.selected)
			},
			isMerchantIndeterminate(merchant) {
				const availableItems = merchant.goodsList.filter(goods => goods.available)
				const selectedCount = availableItems.filter(goods => goods.selected).length
				return selectedCount > 0 && selectedCount < availableItems.length
			},
			async toggleMerchantSelection(merchant, value) {
				await this.updateChecked(
					merchant.goodsList.filter(goods => goods.available),
					value
				)
			},
			async toggleGoodsSelection(goods, value) {
				await this.updateChecked([goods], value)
			},
			async updateChecked(goodsList, checked) {
				const cartItemIds = goodsList.map(goods => goods.cartItemId).filter(Boolean)
				if (!cartItemIds.length) return
				this.setUpdating(cartItemIds, true)
				try {
					await updateMerchantShoppingCartChecked({ cartItemIds, checked })
					goodsList.forEach(goods => {
						goods.selected = checked
					})
				} finally {
					this.setUpdating(cartItemIds, false)
				}
			},
			async changeQuantity(goods, delta) {
				await this.saveQuantity(goods, goods.quantity + delta)
			},
			async handleQuantityInput(goods, event) {
				const quantity = Number.parseInt(event.target.value, 10)
				await this.saveQuantity(goods, Number.isNaN(quantity) ? 1 : quantity)
				event.target.value = goods.quantity
			},
			async saveQuantity(goods, quantity) {
				const nextQuantity = Math.min(99, Math.max(1, quantity))
				if (nextQuantity === goods.quantity || this.isUpdating(goods.cartItemId)) return
				this.setUpdating([goods.cartItemId], true)
				try {
					await updateMerchantShoppingCartQuantity({ cartItemId: goods.cartItemId, quantity: nextQuantity })
					goods.quantity = nextQuantity
				} finally {
					this.setUpdating([goods.cartItemId], false)
				}
			},
			handleRemove(goods) {
				this.$modal
					.confirm(`确认将“${goods.title}”移出购物车吗？`)
					.then(() => this.removeCartItems([goods.cartItemId]))
					.catch(() => {})
			},
			handleBatchRemove() {
				if (!this.selectedItems.length) return
				this.$modal
					.confirm(`确认将选中的 ${this.selectedItems.length} 件商品移出购物车吗？`)
					.then(() => this.removeCartItems(this.selectedItems.map(goods => goods.cartItemId)))
					.catch(() => {})
			},
			async removeCartItems(cartItemIds) {
				if (cartItemIds.length === 1) {
					await removeMerchantShoppingCart({ cartItemId: cartItemIds[0] })
				} else {
					await batchRemoveMerchantShoppingCart({ cartItemIds })
				}
				this.$modal.msgSuccess('已移出购物车')
				await this.loadCart()
			},
			toggleSelectedGoods() {
				this.showAllSelectedGoods = !this.showAllSelectedGoods
			},
			isCouponUsable(coupon) {
				return this.selectedGoodsAmount >= Number(coupon.threshold || 0)
			},
			formatAmount(value) {
				return Number(value || 0).toFixed(2)
			},
			handleImageError(event) {
				event.target.src = DEFAULT_GOODS_IMAGE
			},
			isUpdating(cartItemId) {
				return this.updatingIds.includes(cartItemId)
			},
			setUpdating(cartItemIds, updating) {
				const idSet = new Set(this.updatingIds)
				cartItemIds.forEach(id => (updating ? idSet.add(id) : idSet.delete(id)))
				this.updatingIds = Array.from(idSet)
			},
			// 根据 goodsType 跳转对应详情页。
			handleGoodsClick(goods) {
				const routeName = Number(goods.goodsType) === 2 ? 'MallStoreExhibitionGoodsDetail' : 'MallStoreSelfGoodsDetail'
				this.$router.push({ name: routeName, params: { id: goods.goodsId } })
			},
			// 点击结算即创建20分钟有效的支付单和按店铺拆分的业务订单。
			async handleCheckout() {
				if (!this.selectedItems.length) return this.$modal.msgWarning('请选择需要结算的商品')
				this.settling = true
				try {
					const cartItemIds = this.selectedItems.map(goods => goods.cartItemId)
					const requestSignature = this.selectedItems
						.map(goods => `${goods.cartItemId}:${goods.quantity}`)
						.sort()
						.join('|')
					if (!this.checkoutRequestId || this.checkoutRequestSignature !== requestSignature) {
						this.checkoutRequestId = createUuid()
						this.checkoutRequestSignature = requestSignature
					}
					const response = await createMerchantCheckout({
						sourceType: 'CART',
						cartItemIds,
						requestId: this.checkoutRequestId
					})
					const payOrderId = response.data && response.data.payOrderId
					if (!payOrderId) throw new Error('创建支付单失败')
					this.$router.push({
						name: 'MallStorePayment',
						query: { payOrderId }
					})
				} finally {
					this.settling = false
				}
			}
		}
	}
</script>

<style lang="scss" scoped>
	.shopping-cart-page {
		height: 100%;
		padding: 22px;
		overflow-y: auto;
		color: #263548;
		background: #f4f7fb;
	}
	.cart-page-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 18px;
		h1 {
			margin: 0;
			font-size: 24px;
		}
		p {
			margin: 6px 0 0;
			color: #7d8998;
			font-size: 14px;
		}
	}
	.cart-search {
		display: flex;
		width: 395px;
		.el-input {
			margin-right: 10px;
		}
	}
	.cart-layout {
		display: grid;
		align-items: start;
		grid-template-columns: minmax(0, 1fr) 330px;
		gap: 16px;
	}
	.cart-toolbar,
	.merchant-card,
	.settlement-card,
	.cart-empty {
		overflow: hidden;
		border: 1px solid #e1e8f0;
		border-radius: 10px;
		background: #fff;
		box-shadow: 0 6px 22px rgba(53, 78, 108, 0.05);
	}
	.cart-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		min-height: 58px;
		padding: 0 18px;
		.el-checkbox {
			margin-right: 16px;
		}
		span {
			color: #7d8896;
			font-size: 14px;
		}
		button {
			padding: 8px 0;
			border: 0;
			color: #d95e45;
			font-size: 14px;
			background: transparent;
			cursor: pointer;
			&:disabled {
				color: #afb6c0;
				cursor: not-allowed;
			}
			i {
				margin-right: 4px;
			}
		}
	}
	.merchant-card {
		margin-top: 14px;
	}
	.merchant-heading {
		display: flex;
		align-items: center;
		height: 54px;
		padding: 0 18px;
		border-bottom: 1px solid #e7edf3;
		background: linear-gradient(90deg, #f6faff, #fff);
		.merchant-icon {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 30px;
			height: 30px;
			margin: 0 8px 0 12px;
			border-radius: 8px;
			color: #2074c4;
			font-size: 17px;
			background: #e5f0fb;
		}
		strong {
			font-size: 15px;
		}
		.self-tag {
			margin-left: 8px;
			padding: 3px 7px;
			border-radius: 4px;
			color: #fff;
			font-size: 14px;
			background: #1b74c8;
		}
		.merchant-count {
			margin-left: auto;
			color: #8a95a2;
			font-size: 14px;
		}
	}
	.goods-column-heading,
	.cart-goods-row {
		display: grid;
		align-items: center;
		grid-template-columns: minmax(0, 1fr) 90px 104px 94px 58px;
	}
	.goods-column-heading {
		padding: 10px 15px;
		color: #87919f;
		font-size: 14px;
		text-align: center;
		background: #fafbfd;
		span:first-child {
			text-align: left;
		}
	}
	.cart-goods-row {
		min-height: 130px;
		padding: 16px 15px;
		& + & {
			border-top: 1px dashed #dfe5ec;
		}
		&.is-invalid {
			background: #fafafa;
			.goods-image,
			.unit-price,
			.subtotal {
				opacity: 0.58;
			}
		}
	}
	.goods-information {
		display: flex;
		align-items: center;
		min-width: 0;
		padding-right: 12px;
		cursor: pointer;
	}
	.goods-image {
		flex: 0 0 90px;
		height: 82px;
		margin-left: 10px;
		overflow: hidden;
		border: 1px solid #e3e9ef;
		border-radius: 7px;
		background: #eef2f6;
		cursor: pointer;
		img {
			display: block;
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}
	.goods-copy {
		min-width: 0;
		margin-left: 11px;
		h3 {
			margin: 0 0 6px;
			overflow: hidden;
			font-size: 15px;
			line-height: 21px;
			white-space: nowrap;
			text-overflow: ellipsis;
			cursor: pointer;
			&:hover {
				color: #1972c7;
			}
		}
		p {
			display: -webkit-box;
			margin: 0 0 5px;
			overflow: hidden;
			color: #7d8895;
			font-size: 14px;
			line-height: 19px;
			-webkit-box-orient: vertical;
			-webkit-line-clamp: 2;
		}
		span {
			color: #50789f;
			font-size: 14px;
		}
		.invalid-reason {
			display: block;
			margin-top: 5px;
			color: #d8624b;
			font-size: 14px;
			font-style: normal;
		}
	}
	.unit-price,
	.subtotal {
		font-size: 15px;
		text-align: center;
		small {
			margin-right: 2px;
			font-size: 14px;
		}
	}
	.subtotal {
		color: #e76024;
		font-weight: 600;
	}
	.quantity-control {
		display: flex;
		align-items: center;
		justify-content: center;
		button,
		input {
			height: 30px;
			border: 1px solid #d8e0e8;
			font-size: 14px;
			text-align: center;
			background: #fff;
		}
		button {
			width: 28px;
			color: #58687b;
			cursor: pointer;
			&:first-child {
				border-radius: 4px 0 0 4px;
			}
			&:last-child {
				border-radius: 0 4px 4px 0;
			}
			&:disabled {
				color: #bec4cc;
				background: #f5f6f8;
				cursor: not-allowed;
			}
		}
		input {
			width: 38px;
			margin: 0 -1px;
			outline: none;
			-moz-appearance: textfield;
			&::-webkit-inner-spin-button,
			&::-webkit-outer-spin-button {
				margin: 0;
				-webkit-appearance: none;
			}
		}
	}
	.remove-button {
		padding: 7px 0;
		border: 0;
		color: #6f7c8b;
		font-size: 14px;
		background: transparent;
		cursor: pointer;
		&:hover {
			color: #d95745;
		}
		&:disabled {
			color: #b8c0ca;
			cursor: not-allowed;
		}
		i {
			display: block;
			margin-bottom: 3px;
		}
	}
	.cart-settlement-column {
		position: sticky;
		top: 0;
	}
	.settlement-card {
		padding: 20px;
	}
	.settlement-heading {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		padding-bottom: 16px;
		border-bottom: 1px solid #e5ebf1;
		h2 {
			margin: 0;
			font-size: 19px;
		}
		p {
			margin: 5px 0 0;
			color: #8792a0;
			font-size: 14px;
		}
		button {
			padding: 4px 0;
			border: 0;
			color: #2176c8;
			font-size: 14px;
			background: transparent;
			cursor: pointer;
		}
	}
	.selected-goods-list {
		display: grid;
		gap: 8px;
		padding: 15px 0;
		border-bottom: 1px solid #e5ebf1;
	}
	.selected-goods-card {
		position: relative;
		display: flex;
		align-items: center;
		padding: 8px;
		overflow: hidden;
		border: 1px solid #e1e7ee;
		border-radius: 7px;
		background: #fafcfe;
		img {
			flex: 0 0 48px;
			width: 48px;
			height: 48px;
			border-radius: 5px;
			object-fit: cover;
		}
		> div {
			min-width: 0;
			margin: 0 28px 0 9px;
			strong,
			span {
				display: block;
			}
			strong {
				overflow: hidden;
				font-size: 14px;
				white-space: nowrap;
				text-overflow: ellipsis;
			}
			span {
				margin-top: 5px;
				color: #e06429;
				font-size: 14px;
			}
		}
		> button {
			position: absolute;
			top: 50%;
			right: 9px;
			width: 23px;
			height: 23px;
			padding: 0;
			border: 0;
			border-radius: 50%;
			color: #8f99a5;
			background: #edf1f5;
			cursor: pointer;
			transform: translateY(-50%);
			&:hover {
				color: #fff;
				background: #db6654;
			}
		}
	}
	.selected-empty {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		height: 116px;
		border-bottom: 1px solid #e5ebf1;
		color: #98a2ae;
		i {
			margin-bottom: 8px;
			font-size: 29px;
		}
		span {
			font-size: 14px;
		}
	}
	.coupon-section {
		padding: 17px 0;
		border-bottom: 1px solid #e5ebf1;
	}
	.section-title {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 10px;
		span {
			font-size: 15px;
			font-weight: 600;
		}
		i {
			margin-right: 5px;
			color: #2477c7;
		}
		em {
			color: #d96b2a;
			font-size: 14px;
			font-style: normal;
		}
	}
	.coupon-list {
		display: grid;
		gap: 7px;
	}
	.coupon-placeholder {
		display: flex;
		align-items: center;
		min-height: 48px;
		padding: 9px 11px;
		border: 1px dashed #dbe3eb;
		border-radius: 6px;
		color: #8e99a6;
		font-size: 14px;
		background: #fafbfd;
		i {
			margin-right: 8px;
			color: #91a8be;
			font-size: 18px;
		}
	}
	.coupon-option {
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: 38px;
		padding: 0 11px;
		border: 1px solid #dfe5ec;
		border-radius: 6px;
		font-size: 14px;
		cursor: pointer;
		input {
			display: none;
		}
		strong {
			color: #e05d23;
		}
		&.active {
			border-color: #277bc9;
			color: #1e70bd;
			background: #f1f7fd;
		}
		&.disabled {
			color: #a9b0b9;
			background: #f6f7f8;
			cursor: not-allowed;
			strong {
				color: #a9b0b9;
			}
		}
	}
	.amount-summary {
		padding: 15px 0 10px;
		> div {
			display: flex;
			align-items: center;
			justify-content: space-between;
			height: 31px;
			font-size: 14px;
		}
		span {
			color: #687586;
		}
		strong {
			font-weight: 500;
		}
		.discount-row strong {
			color: #df6028;
		}
	}
	.payable-summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-top: 13px;
		border-top: 1px dashed #dbe2e9;
		> span {
			font-size: 15px;
			font-weight: 600;
		}
		> div {
			color: #e85f22;
			small {
				font-size: 15px;
			}
			strong {
				font-size: 25px;
			}
		}
	}
	.checkout-button {
		width: 100%;
		height: 47px;
		margin-top: 20px;
		border: 0;
		border-radius: 6px;
		font-size: 16px;
		background: linear-gradient(90deg, #176fc8, #2988dd);
		box-shadow: 0 8px 18px rgba(29, 113, 195, 0.18);
	}
	.settlement-tip {
		margin: 11px 0 0;
		color: #929ca8;
		font-size: 14px;
		text-align: center;
		i {
			margin-right: 4px;
			color: #47a273;
		}
	}
	.cart-empty {
		margin-top: 14px;
		padding: 75px 0;
		text-align: center;
		> span {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 68px;
			height: 68px;
			margin: 0 auto 15px;
			border-radius: 50%;
			color: #78a5d1;
			font-size: 31px;
			background: #e7f0fa;
		}
		strong {
			font-size: 17px;
		}
		p {
			margin: 8px 0 18px;
			color: #8a95a3;
			font-size: 14px;
		}
	}
	@media (max-width: 1250px) {
		.cart-layout {
			grid-template-columns: 1fr;
		}
		.cart-settlement-column {
			position: static;
		}
	}
</style>
